import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Typography, Paper, TextField, Button, Grid, CircularProgress, FormControlLabel, Checkbox } from '@mui/material';
import { ArrowBack, Save } from '@mui/icons-material';
import api from '../api/axios';
import toast from 'react-hot-toast';

const UserForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(isEdit);

  const [formData, setFormData] = useState({
    email: '', password: '', first_name: '', last_name: '',
    job_title: '', department: '', entity: '',
    city: '', country: '', mobile: '', is_validator: false
  });

  useEffect(() => {
    if (isEdit) {
      api.get(`/api/v1/users/${id}`).then(res => {
        const u = res.data;
        setFormData({
          email: u.email || '', password: '', first_name: u.first_name || '',
          last_name: u.last_name || '', job_title: u.job_title || '',
          department: u.department || '', entity: u.entity || '',
          city: u.city || '', country: u.country || '',
          mobile: u.mobile || '', is_validator: u.is_validator || false
        });
      }).catch(() => toast.error('Failed to load user')).finally(() => setFetching(false));
    }
  }, [id, isEdit]);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (!formData.email.trim()) { toast.error('Email is required'); return; }
    // Password is optional - will be auto-generated if empty
    if (!formData.first_name.trim()) { toast.error('First name is required'); return; }
    if (!formData.last_name.trim()) { toast.error('Last name is required'); return; }

    setLoading(true);
    try {
      const payload = { ...formData };
      if (isEdit && !payload.password) delete payload.password;
      
      if (isEdit) {
        await api.patch(`/api/v1/users/${id}`, payload);
        toast.success('User updated');
      } else {
        const res = await api.post('/api/v1/users/', payload);
        if (res.data.password) {
          toast.success(`User created! Password: ${res.data.password}`, { duration: 10000 });
          navigator.clipboard.writeText(res.data.password);
          toast('Password copied to clipboard', { duration: 3000 });
        } else {
          toast.success('User created');
        }
      }
      navigate('/users');
      localStorage.setItem("usersNeedRefresh", "true");
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 700, mx: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button onClick={() => navigate('/users')} startIcon={<ArrowBack />} variant="outlined" size="small">Back</Button>
    localStorage.setItem("usersNeedRefresh", "true");
        <Typography variant="h5" fontWeight={600} color="#2c3e50">{isEdit ? '✏️ Edit User' : '🆕 Add User'}</Typography>
      </Box>

      <Paper sx={{ p: 3, borderRadius: '10px', border: '1px solid #e0e0e0' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}><TextField fullWidth label="First Name *" value={formData.first_name} onChange={e => handleChange('first_name', e.target.value)} size="small" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Last Name *" value={formData.last_name} onChange={e => handleChange('last_name', e.target.value)} size="small" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Email *" type="email" value={formData.email} onChange={e => handleChange('email', e.target.value)} size="small" /></Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label={isEdit ? 'New Password (leave empty to keep)' : 'Password (leave empty for auto-generated)'} type="password" value={formData.password} onChange={e => handleChange('password', e.target.value)} size="small" />
          </Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Job Title" value={formData.job_title} onChange={e => handleChange('job_title', e.target.value)} size="small" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Department" value={formData.department} onChange={e => handleChange('department', e.target.value)} size="small" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Entity" value={formData.entity} onChange={e => handleChange('entity', e.target.value)} size="small" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="City" value={formData.city} onChange={e => handleChange('city', e.target.value)} size="small" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Country" value={formData.country} onChange={e => handleChange('country', e.target.value)} size="small" /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Mobile" value={formData.mobile} onChange={e => handleChange('mobile', e.target.value)} size="small" /></Grid>
          <Grid item xs={12}>
            <FormControlLabel control={<Checkbox checked={formData.is_validator} onChange={e => handleChange('is_validator', e.target.checked)} />} label="Validator (can validate analyses)" />
          </Grid>
        </Grid>
      </Paper>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button onClick={() => navigate('/users')} variant="outlined">Cancel</Button>
    localStorage.setItem("usersNeedRefresh", "true");
        <Button onClick={handleSubmit} disabled={loading} variant="contained" startIcon={loading ? <CircularProgress size={16} /> : <Save />} sx={{ bgcolor: '#1abc9c', '&:hover': { bgcolor: '#16a085' } }}>
          {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Create User'}
        </Button>
      </Box>
    </Box>
  );
};

export default UserForm;
